// @flow

import * as React from 'react';
import { find, get, isEmpty } from 'lodash/fp';
import moment from 'moment';

import Responsive from 'app/components/responsive/Responsive';
import SimpleSticky from 'app/components/commons/SimpleSticky';
import PlayerPositionsBar from './PlayerPositionsBar';
import PlayerGamesBar from './PlayerGamesBar';
import PlayerSelectionFilterBar from './PlayerSelectionFilterBar';
import PlayerBoardTable from './PlayerBoardTable';
import PlayerBoardTableHead from './PlayerBoardTableHead';
import GameInfo from './GameInfo';
import RacingScoresDescription from './RacingScoresDescription';

type Props = {
  displayWidget?: boolean,
  gameFilter?: string,
  games?: Array<Object>,
  isRacing: boolean,
  isSticky?: boolean,
  offsetTop: number,
  onGameSelect: () => void,
  onPositionSelect: () => void,
  onWidgetClick: string => void,
  playerBoardTableInfo: Object,
  players?: Array<Object>,
  positionFilter?: string,
  positions?: Array<Object>,
  searchTerm?: string,
  showAllPositionsFilter?: boolean,
  showProjectedScore: boolean,
  widgetPlayerId?: string
};

class PlayerBoard extends React.PureComponent<Props> {
  props: Props;

  board: HTMLElement; // eslint-disable-line react/sort-comp

  static defaultProps = {
    displayWidget: false,
    gameFilter: undefined,
    games: [],
    isSticky: false,
    players: [],
    positionFilter: undefined,
    positions: [],
    searchTerm: undefined,
    showAllPositionsFilter: true,
    widgetPlayerId: undefined
  };

  componentDidUpdate(prevProps: Props) {
    const { positionFilter, searchTerm } = this.props;
    // Ensure that switching to a new tab OR
    // finishing searching for a player resets the scroll position to the top of the list
    if (positionFilter !== prevProps.positionFilter || (!searchTerm && prevProps.searchTerm)) {
      this.board.scrollIntoView(true);
    }
  }

  setBoardRef = (node: HTMLElement) => {
    this.board = node;
  };

  render() {
    const {
      displayWidget,
      gameFilter,
      games,
      isRacing,
      isSticky,
      offsetTop,
      onGameSelect,
      onPositionSelect,
      onWidgetClick,
      playerBoardTableInfo,
      players = [],
      positionFilter,
      positions,
      searchTerm,
      showAllPositionsFilter,
      showProjectedScore,
      widgetPlayerId
    } = this.props;

    const selectedGame = find({ id: gameFilter }, games) || {};
    const { distance, track, weather, name, number, places, venue, startTime } = selectedGame;

    return (
      <div ref={this.setBoardRef}>
        <SimpleSticky isStuckFunc={isStuck => isStuck && isSticky} offsetTop={offsetTop}>
          {isRacing && <RacingScoresDescription />}
          <div>
            {isRacing ? (
              <PlayerGamesBar
                onSelect={onGameSelect}
                games={games}
                isRacing={isRacing}
                selectedGame={gameFilter}
                showSelectionFeedback={false}
                showFilled={false}
                showAllTab
              />
            ) : (
              <PlayerPositionsBar
                onSelect={onPositionSelect}
                positions={positions}
                showAllTab={showAllPositionsFilter}
                selectedPosition={!searchTerm ? positionFilter : undefined}
              />
            )}
            {!isEmpty(selectedGame) && (
              <GameInfo
                distance={distance}
                number={number}
                track={track}
                weather={weather}
                name={name}
                places={places}
                venue={venue}
                startTime={moment(startTime).format('h:mm a')}
              />
            )}
            <PlayerSelectionFilterBar
              isRacing={isRacing}
              gameFilter={get('name', find({ id: gameFilter }, games))}
              positionFilter={get('name', find({ id: positionFilter }, positions))}
              showProjectedScoreToggler={isRacing}
            />
            <Responsive tabletLandscape desktop>
              <PlayerBoardTableHead />
            </Responsive>
          </div>
        </SimpleSticky>
        <Responsive mobile>
          {isMobile => (
            <PlayerBoardTable
              displayWidget={displayWidget}
              playerBoardTableInfo={playerBoardTableInfo}
              players={players}
              onWidgetClick={onWidgetClick}
              showProjectedScore={showProjectedScore}
              widgetPlayerId={widgetPlayerId}
              positionFilter={positionFilter}
              isMobile={isMobile}
              isRacing={isRacing}
            />
          )}
        </Responsive>
      </div>
    );
  }
}

export default PlayerBoard;
